import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CheckCheck } from "lucide-react";

// WhatsApp's own tick colors — a fixed brand reference, not a design-system
// token: light gray for sent/delivered, its signature blue once seen.
const TICK_GRAY = "rgba(255,255,255,0.75)";
const TICK_BLUE = "#53bdeb";

/**
 * Compact per-bubble delivery indicator for the current user's own
 * messages, WhatsApp-style: one gray check once sent, two gray checks once
 * delivered, two blue checks once seen. Ported from mobile's
 * `chat-ticks/index.js` UX (tap reveals the raw counts for 4 seconds) but
 * with WhatsApp's familiar tri-state coloring instead of mobile's single
 * always-blue-when-seen double check.
 */
const MessageTicks = ({ message }) => {
  const { t } = useTranslation("chat");
  const [showCounts, setShowCounts] = useState(false);

  useEffect(() => {
    if (!showCounts) return undefined;
    const id = setTimeout(() => setShowCounts(false), 4000);
    return () => clearTimeout(id);
  }, [showCounts]);

  // seenByAll/deliveredToAll come straight from the hub (confirmed live —
  // see chatHelpers.js) and are the only correct signal for the tick color:
  // blue must mean every recipient has seen it, not just one. The previous
  // `|| seenCount > 0` / `|| deliveredCount > 0` fallbacks turned the tick
  // blue/double-gray the moment a single recipient saw/received it.
  const deliveredCount = message.deliveredTo?.length || 0;
  const seenCount = message.seenBy?.length || 0;
  const seen = message.seenByAll === true;
  const delivered = message.deliveredToAll === true;

  return (
    <button type="button" style={s.btn} onClick={(e) => { e.stopPropagation(); setShowCounts((v) => !v); }}>
      {showCounts ? (
        <span style={s.counts}>{t("ticks.delivered")} {deliveredCount} · {t("ticks.seen")} {seenCount}</span>
      ) : seen || delivered ? (
        <CheckCheck size={14} strokeWidth={2.5} color={seen ? TICK_BLUE : TICK_GRAY} />
      ) : (
        <Check size={14} strokeWidth={2.5} color={TICK_GRAY} />
      )}
    </button>
  );
};

const s = {
  btn: { background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center" },
  counts: { fontSize: 10, color: "rgba(255,255,255,0.85)", whiteSpace: "nowrap" },
};

export default MessageTicks;
